import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, ExternalLink } from "lucide-react";
import { GroupLogo } from "@/components/Logo";
import { useEnquiry } from "@/context/EnquiryContext";
import { EXTERNAL_URLS } from "@/config/site";

const navItems = [
  { label: "Home", to: "/" },
  { label: "About Us", to: "/about" },
  { label: "Our Organizations", to: "/organizations" },
];

const orgLinks = [
  { label: "ATS | IT Solutions", href: EXTERNAL_URLS.ATS, external: true },
  { label: "ASAI", href: EXTERNAL_URLS.ASAI, external: true },
  { label: "AIMT", href: EXTERNAL_URLS.AIMT, external: true },
  { label: "Atmasamman Foundation", href: "/foundation", external: false },
];

const tailItems = [
  { label: "Careers", to: "/careers" },
  { label: "Contact Us", to: "/contact" },
];

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [orgOpen, setOrgOpen] = useState(false);
  const { openEnquiry } = useEnquiry();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOrgOpen(false);
  }, [location.pathname]);

  const linkBase =
    "text-sm font-medium text-white/85 hover:text-white transition-colors relative py-2";

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-[#0B132B]/95 backdrop-blur-xl border-b border-white/10 shadow-lg shadow-black/20" : "bg-[#0B132B]/70 backdrop-blur-md"
      }`}
      data-testid="main-navbar"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
        <Link to="/" data-testid="nav-home-logo" className="shrink-0">
          <GroupLogo className="h-11" withText />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
          {navItems.map((item) => (
            <Link key={item.to} to={item.to} className={linkBase} data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}>
              {item.label}
            </Link>
          ))}

          <div className="relative" onMouseEnter={() => setOrgOpen(true)} onMouseLeave={() => setOrgOpen(false)}>
            <button className={`${linkBase} inline-flex items-center gap-1`} data-testid="nav-orgs-dropdown-trigger" aria-expanded={orgOpen}>
              Organizations <ChevronDown className="h-4 w-4" />
            </button>
            {orgOpen && (
              <div className="absolute left-1/2 top-full w-64 -translate-x-1/2 pt-3" data-testid="nav-orgs-dropdown">
                <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl shadow-black/20">
                  {orgLinks.map((o) =>
                    o.external ? (
                      <a key={o.label} href={o.href} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between px-4 py-3 text-sm font-medium text-slate-700 hover:bg-orange-50 hover:text-[#0B132B]" data-testid={`nav-org-${o.label.split(" ")[0].toLowerCase()}`}>
                        {o.label} <ExternalLink className="h-3.5 w-3.5 text-orange-500" />
                      </a>
                    ) : (
                      <Link key={o.label} to={o.href} className="flex items-center px-4 py-3 text-sm font-medium text-slate-700 hover:bg-orange-50 hover:text-[#0B132B]" data-testid="nav-org-foundation">
                        {o.label}
                      </Link>
                    )
                  )}
                </div>
              </div>
            )}
          </div>

          {tailItems.map((item) => (
            <Link key={item.to} to={item.to} className={linkBase} data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}>
              {item.label}
            </Link>
          ))}

          <button
            onClick={() => openEnquiry({ interest: "Atmasamman Group" })}
            data-testid="nav-request-info-btn"
            className="rounded-xl bg-orange-500 px-5 py-2.5 text-sm font-semibold text-white shadow-md transition-all hover:bg-orange-600 hover:-translate-y-0.5"
          >
            REQUEST INFORMATION
          </button>
        </nav>

        {/* Mobile toggle */}
        <button
          className="inline-flex items-center justify-center rounded-lg p-2 text-white lg:hidden"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="border-t border-white/10 bg-[#0B132B] lg:hidden" data-testid="mobile-menu">
          <nav className="mx-auto max-w-7xl space-y-1 px-4 py-4" aria-label="Mobile">
            {[...navItems, ...tailItems].map((item) => (
              <Link key={item.to} to={item.to} className="block rounded-lg px-3 py-3 text-base font-medium text-white/90 hover:bg-white/5" data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}>
                {item.label}
              </Link>
            ))}
            <div className="px-3 pb-1 pt-3 font-mono text-xs uppercase tracking-[0.2em] text-orange-400">Our Organizations</div>
            {orgLinks.map((o) =>
              o.external ? (
                <a key={o.label} href={o.href} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between rounded-lg px-3 py-3 text-base font-medium text-white/80 hover:bg-white/5" data-testid={`mobile-org-${o.label.split(" ")[0].toLowerCase()}`}>
                  {o.label} <ExternalLink className="h-4 w-4 text-orange-400" />
                </a>
              ) : (
                <Link key={o.label} to={o.href} className="block rounded-lg px-3 py-3 text-base font-medium text-white/80 hover:bg-white/5" data-testid="mobile-org-foundation">
                  {o.label}
                </Link>
              )
            )}
            <button
              onClick={() => openEnquiry({ interest: "Atmasamman Group" })}
              data-testid="mobile-request-info-btn"
              className="mt-3 w-full rounded-xl bg-orange-500 px-5 py-3 text-base font-semibold text-white hover:bg-orange-600"
            >
              REQUEST INFORMATION
            </button>
          </nav>
        </div>
      )}
    </header>
  );
}
